import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row } from "react-bootstrap";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Cell,
} from "recharts";

const Graph = ({ data, qData, isEnglish = false }) => {
    const [values, setValues] = useState([]);
    const [isCollapsed, setIsCollapsed] = useState(true);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    useEffect(() => {
        if (data && qData) {
            doSomething();
        }
    }, [data, qData, isEnglish]);

    const doSomething = () => {
        const normalize = (str) => {
            if (str == null) return "";
            const s = String(str)
                .replace(/[“”"']/g, "")
                .trim()
                .toLowerCase();
            return s;
        };

        const isNumericValue = (value) => /^[0-9]+$/.test(String(value).trim());

        const getOptionText = (opt, fallbackIndex) => {
            if (opt == null) return String(fallbackIndex);
            if (typeof opt === "string") return opt;
            if (typeof opt === "object") {
                if (opt.option != null) return String(opt.option);
                if (opt.label != null) return String(opt.label);
                if (opt.name != null) return String(opt.name);
            }
            return String(fallbackIndex);
        };

        const options = qData.options || [];
        const optionLabels = options.map((opt, idx) => getOptionText(opt, idx + 1));
        const isNumericScaleQuestion =
            optionLabels.length > 0 && optionLabels.every((label) => isNumericValue(label));

        // Build mappings from option id and option label -> 0-based index
        const idToIndex = new Map(
            options.map((opt, idx) => [String(opt?._id ?? idx), idx])
        );
        const labelToIndex = new Map(
            options.map((opt, idx) => [normalize(getOptionText(opt, idx + 1)), idx])
        );

        const countsByBucket = options.map(() => 0);

        // Group answers by submission key to avoid duplicate API rows for the same response
        const groupedAnswers = new Map();
        (data || []).forEach((entry, idx) => {
            const answerMeta = entry?.surveyAnswer || {};
            const submissionKey =
                answerMeta._id ||
                `${answerMeta.createdBy || "unknown"}-${answerMeta.questionId || "unknown"}-${answerMeta.createdAt || idx}`;
            const raw = answerMeta.answer;
            const answersArray = Array.isArray(raw) ? raw : [raw];
            const bucketIndexes = answersArray
                .filter((val) => val != null)
                .map((val) => {
                    const asString = String(val);
                    let bucketIndex = idToIndex.get(asString);
                    if (bucketIndex == null) {
                        bucketIndex = labelToIndex.get(normalize(asString));
                    }
                    return bucketIndex;
                })
                .filter((bucketIndex) => bucketIndex != null);

            if (!groupedAnswers.has(submissionKey)) {
                groupedAnswers.set(submissionKey, []);
            }
            groupedAnswers.get(submissionKey).push(...bucketIndexes);
        });

        groupedAnswers.forEach((bucketIndexes) => {
            const uniqueIndexes = [...new Set(bucketIndexes)];
            if (uniqueIndexes.length === 0) return;

            // Temporary safeguard for backend duplicate rows on single rating answer.
            // For numeric scales, keep only the highest selected index per submission.
            if (isNumericScaleQuestion && uniqueIndexes.length > 1) {
                const finalIndex = Math.max(...uniqueIndexes);
                countsByBucket[finalIndex] += 1;
                return;
            }

            uniqueIndexes.forEach((index) => {
                countsByBucket[index] += 1;
            });
        });

        const answerLabel = isEnglish ? "Answer" : "Risposta";

        const newArr = options.map((opt, index) => ({
            name: getOptionText(opt, index + 1),
            [answerLabel]: countsByBucket[index],
        }));

        setValues(newArr);
    };

    const answerLabel = isEnglish ? "Answer" : "Risposta";
    const maxPv = Math.max(...values.map((item) => item[answerLabel]), 0);

    return (
        <>
            <div className="d-flex">
                <BarChart
                    width={500}
                    height={260}
                    data={values}
                    margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Legend />
                    {/* <Tooltip /> */}
                    <Bar dataKey={answerLabel} barSize={10}>
                        {values.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry[answerLabel] === maxPv ? "#20C6FB" : "#7E879F"}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </div>
            {qData?.comment && (
                <Row className="mt-3 mb-3">
                    <Col>
                        <h6
                            onClick={toggleCollapse}
                            style={{
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            {isEnglish ? "Comments" : "Commenti"}
                            <span style={{ cursor: "pointer", marginLeft: "10px" }}>
                                {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                            </span>
                        </h6>
                        {!isCollapsed &&
                            data &&
                            data.length > 0 &&
                            data.map((elem, i) => (
                                elem?.surveyAnswer?.comment && (
                                    <Card style={{ marginBottom: "5px" }} key={i}>
                                        <CardBody>{elem?.surveyAnswer?.comment}</CardBody>
                                    </Card>
                                )
                            ))}
                    </Col>
                </Row>
            )}
        </>
    );
};

export default Graph;
